import { useMemo } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import type { PartnerTemplate } from "@/components/confluence/templates/partner-template-card";
import { PartnerTemplateCardCarousel } from "@/components/confluence/templates/partner-template-card-carousel";
import { TemplateCategory } from "@/components/confluence/templates/template-categories-search";
import { Template } from "@/content/templates/template.types";
import { AdditionalCategoriesSection } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/additional-categories-section.tsx";
import {
    partnerTemplatesDataUrlSlugs,
    testPartnerTemplatesData
} from "@/templates/software/confluence/templates/confluence-templates-page/src/test-data/test-data.ts";

interface TemplateLearnFromLeadersSectionProps {
    templates: Template[];
    categories: TemplateCategory[];
}

const sectionStyles = css({
    position: "relative",
    padding: "32px 0px 0px 0px"
});

const textContentSection = css({
    backgroundImage:
        "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/partnerbackground.svg)",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "bottom center",
    position: "relative",
    zIndex: 1
});

const headerStyles = css({
    "color": "#1C2B42",
    "paddingTop": "40px",
    "margin-bottom": "60px",
    "textAlign": "center",
    "lineHeight": "48px",
    "fontFamily": "var(--bxp-charlie-display-font-family)",
    "fontSize": "40px",
    "fontWeight": 700,
    "@media (max-width: 639.99px)": {
        width: "288px",
        margin: "0 auto 40px",
        fontSize: "1.5rem"
    },
    "@media (min-width: 640px) and (max-width: 767.99px)": {
        width: "592px",
        margin: "0 auto 40px"
    },
    "@media (min-width: 768px) and (max-width: 1023.99px)": {
        width: "592px",
        margin: "0 auto 40px"
    }
});

const partnerSectionStyles = css({
    padding: "0px 0px 112px px"
});

const TemplateLearnFromLeadersSection = ({
    templates,
    categories
}: TemplateLearnFromLeadersSectionProps) => {
    // We are grabbing the partner templates from the whole list of templates returned in the template loader and filtering them out. Note that some fields are still using the existing hardcoded partner template data, as the images or icons are not rendering properly.
    //  @ts-ignore
    const partnerTemplates: PartnerTemplate[] = useMemo(
        () =>
            templates
                ?.map((template: Template) => {
                    const testData = testPartnerTemplatesData.find(
                        (x) => x.urlSlug === template.urlSlug
                    );
                    // Use CPAPI data where possible, otherwise use hardcoded data
                    return {
                        templateId: template.templateId,
                        name: template.name,
                        urlSlug: template.urlSlug,
                        partnerDescription: template.oneLinerHeadline,
                        contributor: testData?.contributor,
                        previewBlurb: template.oneLinerHeadline,
                        templateIcon: testData?.templateIcon,
                        partnerImage: testData?.partnerImage
                    };
                })
                .sort((a, b) => {
                    return (
                        partnerTemplatesDataUrlSlugs.indexOf(a.urlSlug ?? "") -
                        partnerTemplatesDataUrlSlugs.indexOf(b.urlSlug ?? "")
                    );
                }),
        [templates]
    );

    return (
        <section css={sectionStyles}>
            <div css={textContentSection}>
                <h2 css={headerStyles}>
                    <FormattedMessage
                        id="confluence.templates.learn.from.industry.leaders"
                        defaultMessage="Learn from industry leaders"
                    />
                </h2>
                <div css={partnerSectionStyles}>
                    <PartnerTemplateCardCarousel
                        partnerTemplates={partnerTemplates}
                    />
                </div>
            </div>
            <AdditionalCategoriesSection categories={categories} />
        </section>
    );
};

TemplateLearnFromLeadersSection.displayName = "TemplateLearnFromLeadersSection";

export {
    TemplateLearnFromLeadersSection,
    TemplateLearnFromLeadersSectionProps
};
